import { ENVIRONMENTS } from "../../../Common/config";
import { getErrorMsg } from "../../../Common/utils/common";
import { ApplovinClientUserData, ApplovinEventName } from "../../../Common/models/applovin";
import { bugNotif } from "../../api/bugsnag";
import { applovinTrackEvent } from "../../api/applovinEvents";

const isProductionEnv = () => import.meta.env.MODE === ENVIRONMENTS.production;

const ONE_YEAR_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

export const generateDedupeId = () => Math.random().toString(36).substring(2, 17);

const getCookieValue = (cookieName: string): string | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${cookieName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : undefined;
};

const setCookieValue = (cookieName: string, value: string): void => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${cookieName}=${encodeURIComponent(value)}; path=/; max-age=${ONE_YEAR_MAX_AGE_SECONDS}; SameSite=Lax`;
};

export const getApplovinAttributionUserData = (): ApplovinClientUserData => {
  if (typeof window === "undefined") {
    return {};
  }

  const params = new URLSearchParams(window.location.search);
  const aleidFromQuery = params.get("aleid");
  const alartFromQuery = params.get("alart");

  if (aleidFromQuery) {
    setCookieValue("_axeid", aleidFromQuery);
  }

  if (alartFromQuery) {
    setCookieValue("_alart", alartFromQuery);
  }

  const axwrt = getCookieValue("_axwrt");
  const aleid = aleidFromQuery ?? getCookieValue("_axeid") ?? undefined;
  const alart = alartFromQuery ?? getCookieValue("_alart") ?? undefined;

  return {
    ...(axwrt ? { axwrt } : {}),
    ...(aleid ? { aleid } : {}),
    ...(alart ? { alart } : {}),
  };
};

export const appendApplovinClickIdsToUrl = (url: string): string => {
  const { aleid, alart } = getApplovinAttributionUserData();
  let result = url;

  if (aleid) {
    result = `${result}&aleid=${encodeURIComponent(aleid)}`;
  }

  if (alart) {
    result = `${result}&alart=${encodeURIComponent(alart)}`;
  }

  return result;
};

export const trackAxonEvent = (
  eventName: string,
  payload: Record<string, unknown>,
  dedupeId?: string
): void => {
  if (!isProductionEnv()) {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  const axon = (window as Window & { axon?: (...args: unknown[]) => void }).axon;
  if (typeof axon !== "function") {
    return;
  }

  try {
    if (dedupeId) {
      axon("track", eventName, payload, { dedupe_id: dedupeId });
    } else {
      axon("track", eventName, payload);
    }
  } catch (error) {
    console.log(`error tracking Axon event: ${eventName}`, error);
  }
};

export const trackApplovinConversionEvent = async (
  eventName: ApplovinEventName,
  payload: Record<string, unknown>,
  user: ApplovinClientUserData,
  dedupeId?: string
): Promise<void> => {
  if (!isProductionEnv()) {
    return;
  }

  if (typeof window === "undefined") {
    return;
  }

  try {
    await applovinTrackEvent({
      name: eventName,
      dedupe_id: dedupeId,
      event_source_url: window.location.href,
      data: payload,
      user: {
        ...getApplovinAttributionUserData(),
        ...user,
      },
    });
  } catch (error) {
    console.log(`error tracking AppLovin CAPI event: ${eventName}`, error);
    bugNotif("trackApplovinConversionEvent", getErrorMsg(error).rawErrorMsg);
  }
};
